import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SliderComponent } from '@examdojo/core/widget';
import katex from 'katex';
import {
  DEFAULT_VENN_DIAGRAM_DISPLAY,
  DEFAULT_VENN_DIAGRAM_PARAMS,
  VennDiagramDisplayConfig,
  VennDiagramParams,
  VennMembership,
  buildMembership,
  membershipKey,
  regionToMembership,
  resolveHighlightedRegions,
} from './venn-diagram.model';

const renderLatex = (latex: string): string => katex.renderToString(latex, { throwOnError: false, output: 'html' });

// ── 2-set layout ──────────────────────────────────────────
const VIEWBOX = 320;
const CX_A = VIEWBOX * 0.38;
const CX_B = VIEWBOX * 0.62;
const CY = VIEWBOX * 0.48;
const R = VIEWBOX * 0.26;
const PAD = VIEWBOX * 0.05;
const RECT_X = PAD;
const RECT_Y = PAD;
const RECT_W = VIEWBOX - 2 * PAD;
const RECT_H = VIEWBOX - 2 * PAD;
const RECT_RX = 6;

// ── Proportional-mode constants ─────────────────────────
const PROP_BASE_R = 80;
const PROP_MAX_R = 120;

const ORANGE = '\\color{#f97316}';
const INDIGO = '\\color{#6366f1}';

function probRadius(p: number): number {
  if (p <= 0) {
    return 0;
  }
  return Math.min(PROP_BASE_R * Math.sqrt(p), PROP_MAX_R);
}

function round(n: number): number {
  return Math.round(n * 1e6) / 1e6;
}

function fmt(n: number): string {
  return parseFloat(n.toFixed(3)).toString();
}

// ── 3-set layout (equilateral triangle arrangement) ───────
const CENTER3_X = VIEWBOX / 2;
const CENTER3_Y = VIEWBOX * 0.52;
const D3 = VIEWBOX * 0.155;
const R3 = VIEWBOX * 0.205;
const CX3_A = CENTER3_X;
const CY3_A = CENTER3_Y - D3;
const CX3_B = CENTER3_X - (D3 * Math.sqrt(3)) / 2;
const CY3_B = CENTER3_Y + D3 / 2;
const CX3_C = CENTER3_X + (D3 * Math.sqrt(3)) / 2;
const CY3_C = CENTER3_Y + D3 / 2;

type InternalRegion = 'onlyA' | 'onlyB' | 'intersection' | 'outside';

let nextId = 0;

@Component({
  selector: 'dojo-venn-diagram',
  standalone: true,
  imports: [SliderComponent],
  templateUrl: './venn-diagram.component.html',
  styleUrl: './venn-diagram.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'block',
    '[class.venn-diagram--interactive]': 'isInteractive()',
  },
})
export class VennDiagramComponent {
  constructor(private readonly sanitizer: DomSanitizer) {
    effect(
      () => {
        const probs = this.params().probabilities;
        if (probs) {
          this.pA.set(probs.initialPA);
          this.pB.set(probs.initialPB);
          this.pAB.set(probs.initialPAB);
        }
      },
      { allowSignalWrites: true },
    );
  }

  readonly params = input<VennDiagramParams>(DEFAULT_VENN_DIAGRAM_PARAMS);
  readonly display = input<VennDiagramDisplayConfig>(DEFAULT_VENN_DIAGRAM_DISPLAY);

  readonly regionHover = output<VennMembership | null>();
  readonly regionClick = output<VennMembership>();

  /** Unique prefix for clip-path IDs so multiple instances don't collide. */
  readonly uid = `venn-${nextId++}`;

  /** Currently hovered region key (null = none). */
  readonly hoveredRegion = signal<string | null>(null);

  // ── Shared layout constants ────────────────────────────
  readonly viewBox = VIEWBOX;
  readonly svgViewBox = computed(() => {
    if (this.isInteractive()) {
      return `${PAD} ${PAD} ${VIEWBOX - PAD} ${VIEWBOX - PAD}`;
    }
    return `0 0 ${VIEWBOX} ${VIEWBOX}`;
  });
  readonly rectX = RECT_X;
  readonly rectY = RECT_Y;
  readonly rectW = RECT_W;
  readonly rectH = RECT_H;
  readonly rectRx = RECT_RX;

  // ── Proportional mode ─────────────────────────────────
  readonly isProportional = computed(() => this.params().probabilities !== undefined);
  readonly isInteractive = computed(() => this.display().interactive === true);

  readonly pA = signal(0.5);
  readonly pB = signal(0.4);
  readonly pAB = signal(0.2);

  readonly minOverlap = computed(() => round(Math.max(0, this.pA() + this.pB() - 1)));
  readonly maxOverlap = computed(() => round(Math.min(this.pA(), this.pB())));

  // Derived probabilities
  readonly pAgivenB = computed(() => (this.pB() > 0 ? this.pAB() / this.pB() : 0));
  readonly pBgivenA = computed(() => (this.pA() > 0 ? this.pAB() / this.pA() : 0));
  readonly product = computed(() => this.pA() * this.pB());
  readonly isIndependent = computed(() => Math.abs(this.pAB() - this.product()) < 0.005);

  // Region labels for proportional mode (KaTeX-rendered)
  private readonly pAonlyVal = computed(() => fmt(this.pA() - this.pAB()));
  private readonly pBonlyVal = computed(() => fmt(this.pB() - this.pAB()));
  private readonly pABval = computed(() => fmt(this.pAB()));
  readonly pAonlyHtml = computed(() =>
    this.sanitizer.bypassSecurityTrustHtml(renderLatex(`{${ORANGE}${this.pAonlyVal()}}`)),
  );
  readonly pABhtml = computed(() =>
    this.sanitizer.bypassSecurityTrustHtml(renderLatex(`{\\color{#a855f7}${this.pABval()}}`)),
  );
  readonly pBonlyHtml = computed(() =>
    this.sanitizer.bypassSecurityTrustHtml(renderLatex(`{${INDIGO}${this.pBonlyVal()}}`)),
  );
  // Slider KaTeX labels
  readonly sliderLabelPA = computed(() => this.sanitizer.bypassSecurityTrustHtml(renderLatex(`P({${ORANGE}A})`)));
  readonly sliderLabelPB = computed(() => this.sanitizer.bypassSecurityTrustHtml(renderLatex(`P({${INDIGO}B})`)));
  readonly sliderLabelPAB = computed(() =>
    this.sanitizer.bypassSecurityTrustHtml(renderLatex(`P({${ORANGE}A} \\cap {${INDIGO}B})`)),
  );

  // KaTeX formula signals
  readonly formulaAgivenB = computed<SafeHtml>(() => {
    const latex =
      this.pB() > 0
        ? `P({${ORANGE}A} \\mid {${INDIGO}B}) = \\dfrac{${this.pABval()}}{${fmt(this.pB())}} = ${fmt(this.pAgivenB())}`
        : `P({${ORANGE}A} \\mid {${INDIGO}B}) = \\text{undefined}`;
    return this.sanitizer.bypassSecurityTrustHtml(renderLatex(latex));
  });

  readonly formulaBgivenA = computed<SafeHtml>(() => {
    const latex =
      this.pA() > 0
        ? `P({${INDIGO}B} \\mid {${ORANGE}A}) = \\dfrac{${this.pABval()}}{${fmt(this.pA())}} = ${fmt(this.pBgivenA())}`
        : `P({${INDIGO}B} \\mid {${ORANGE}A}) = \\text{undefined}`;
    return this.sanitizer.bypassSecurityTrustHtml(renderLatex(latex));
  });

  readonly formulaIndep = computed<SafeHtml>(() => {
    const pab = this.pABval();
    const prod = fmt(this.product());
    const symbol = this.isIndependent() ? '=' : '\\neq';
    const color = this.isIndependent() ? INDIGO : '\\color{#6b7280}';
    const label = this.isIndependent() ? '\\text{Independent}' : '\\text{Not independent}';
    const latex = `P({${ORANGE}A} \\cap {${INDIGO}B}) = ${pab} ${symbol} ${prod} = P({${ORANGE}A}) \\cdot P({${INDIGO}B}) \\quad {${color}{${label}}}`;
    return this.sanitizer.bypassSecurityTrustHtml(renderLatex(latex));
  });

  // ── 2-set layout (reactive for mutually exclusive & proportional mode) ──
  private readonly isMutuallyExclusive = computed(() => this.display().mutuallyExclusive === true);

  /** Proportional overlap positioning — distance between circle centers. */
  private readonly propLayout = computed(() => {
    const rA = probRadius(this.pA());
    const rB = probRadius(this.pB());
    const pAB = this.pAB();
    const pA = this.pA();
    const pB = this.pB();

    if (pAB <= 0 || rA <= 0 || rB <= 0) {
      return { cxA: VIEWBOX * 0.33, cxB: VIEWBOX * 0.67, rA, rB };
    }

    const overlapRatio = Math.min(pAB / Math.min(pA, pB), 1);
    const dMax = rA + rB;
    const dMin = Math.abs(rA - rB);
    const d = dMax - overlapRatio * (dMax - dMin);
    const midX = VIEWBOX / 2;
    return { cxA: midX - d / 2, cxB: midX + d / 2, rA, rB };
  });

  readonly rA = computed(() => {
    if (this.isProportional()) {
      return this.propLayout().rA;
    }
    return this.isMutuallyExclusive() ? VIEWBOX * 0.18 : R;
  });

  readonly rB = computed(() => {
    if (this.isProportional()) {
      return this.propLayout().rB;
    }
    return this.isMutuallyExclusive() ? VIEWBOX * 0.18 : R;
  });

  readonly cxA = computed(() => {
    if (this.isProportional()) {
      return this.propLayout().cxA;
    }
    return this.isMutuallyExclusive() ? VIEWBOX * 0.3 : CX_A;
  });

  readonly cxB = computed(() => {
    if (this.isProportional()) {
      return this.propLayout().cxB;
    }
    return this.isMutuallyExclusive() ? VIEWBOX * 0.7 : CX_B;
  });

  readonly cy = CY;

  readonly labelAx = computed(() => {
    if (this.isMutuallyExclusive()) {
      return this.cxA();
    }
    return this.cxA() - this.rA() * 0.5;
  });
  readonly labelAy = computed(() => CY + this.rA() + 18);
  readonly labelBx = computed(() => {
    if (this.isMutuallyExclusive()) {
      return this.cxB();
    }
    return this.cxB() + this.rB() * 0.5;
  });
  readonly labelBy = computed(() => CY + this.rB() + 18);
  readonly labelUx = PAD + 10;
  readonly labelUy = PAD + 10;

  // ── 3-set layout ──────────────────────────────────────
  readonly cx3A = CX3_A;
  readonly cy3A = CY3_A;
  readonly cx3B = CX3_B;
  readonly cy3B = CY3_B;
  readonly cx3C = CX3_C;
  readonly cy3C = CY3_C;
  readonly r3 = R3;
  readonly label3Ax = CX3_A;
  readonly label3Ay = CY3_A - R3 - 6;
  readonly label3Bx = CX3_B - R3 * 0.55;
  readonly label3By = CY3_B + R3 + 18;
  readonly label3Cx = CX3_C + R3 * 0.55;
  readonly label3Cy = CY3_C + R3 + 18;

  // ── Computed helpers ─────────────────────────────────────

  readonly sets = computed(() => this.params().sets ?? ['A', 'B']);
  /** Clamped to [2, 3] — the number of circles we actually draw. */
  readonly renderedSetCount = computed(() => Math.min(Math.max(this.sets().length, 2), 3));
  /** The first N sets that we actually render circles for. */
  private readonly renderedSets = computed(() => this.sets().slice(0, this.renderedSetCount()));

  readonly largeLabels = computed(() => this.display().largeLabels === true);

  readonly labelAHtml = computed(() =>
    this.sanitizer.bypassSecurityTrustHtml(renderLatex(this.renderedSets()[0] ?? 'A')),
  );
  readonly labelBHtml = computed(() =>
    this.sanitizer.bypassSecurityTrustHtml(renderLatex(this.renderedSets()[1] ?? 'B')),
  );
  readonly labelCHtml = computed(() =>
    this.sanitizer.bypassSecurityTrustHtml(renderLatex(this.renderedSets()[2] ?? 'C')),
  );
  readonly labelUHtml = computed(() =>
    this.sanitizer.bypassSecurityTrustHtml(renderLatex(this.params().labelU ?? 'U')),
  );

  readonly highlightColor = computed(() => this.display().highlightColor ?? null);

  // ── Per-set shading ─────────────────────────────────
  readonly shadingA = computed(() => this.display().setShading?.[this.sets()[0]] ?? null);
  readonly shadingB = computed(() => this.display().setShading?.[this.sets()[1]] ?? null);
  readonly strokeA = computed(() => this.shadingA() ?? this.colorA() ?? 'var(--venn-stroke)');
  readonly strokeB = computed(() => this.shadingB() ?? this.colorB() ?? 'var(--venn-stroke)');

  readonly shadingIntersectionOnly = computed(() => this.display().shadingIntersectionOnly === true);

  // ── Per-set solid tint fills ──────────────────────
  readonly colorA = computed(() => this.display().setColors?.[this.sets()[0]] ?? null);
  readonly colorB = computed(() => this.display().setColors?.[this.sets()[1]] ?? null);

  // ── 2-set highlighting ────────────────────────────────
  private readonly highlighted2 = computed(() => resolveHighlightedRegions(this.params()));

  readonly outsideFill = computed(() => this.regionFill('outside'));
  readonly onlyAFill = computed(() => this.regionFill('onlyA'));
  readonly onlyBFill = computed(() => this.regionFill('onlyB'));
  readonly intersectionFill = computed(() => this.regionFill('intersection'));

  readonly outsideOpacity = computed(() => this.regionOpacity('outside'));
  readonly onlyAOpacity = computed(() => this.regionOpacity('onlyA'));
  readonly onlyBOpacity = computed(() => this.regionOpacity('onlyB'));
  readonly intersectionOpacity = computed(() => this.regionOpacity('intersection'));

  // ── Per-set colors for proportional mode ──────────────
  readonly propColorA = computed(() => (this.isProportional() ? 'rgba(249, 115, 22, 0.15)' : null));
  readonly propColorB = computed(() => (this.isProportional() ? 'rgba(99, 102, 241, 0.15)' : null));
  readonly propStrokeA = computed(() => (this.isProportional() ? '#f97316' : null));
  readonly propStrokeB = computed(() => (this.isProportional() ? '#6366f1' : null));

  // Proportional region label positions
  readonly propMidX = computed(() => (this.cxA() + this.cxB()) / 2);
  readonly showPAonly = computed(() => this.pA() - this.pAB() > 0.005 && this.rA() > 0);
  readonly showPAB = computed(() => this.pAB() > 0.005);
  readonly showPBonly = computed(() => this.pB() - this.pAB() > 0.005 && this.rB() > 0);
  // ── 3-set highlighting (projects membership to rendered sets) ──
  private readonly highlightedKeys = computed(() => {
    const rendered = this.renderedSets();
    return new Set((this.params().highlighted ?? []).map((m) => membershipKey(m, rendered)));
  });

  // ── Slider callbacks ─────────────────────────────────

  onPAChange(value: number): void {
    this.pA.set(value);
    this.clampPAB(value, this.pB());
  }

  onPBChange(value: number): void {
    this.pB.set(value);
    this.clampPAB(this.pA(), value);
  }

  onPABChange(value: number): void {
    const lo = this.minOverlap();
    const hi = this.maxOverlap();
    this.pAB.set(round(Math.max(lo, Math.min(value, hi))));
  }

  // ── 2-set event handlers ──────────────────────────────

  onRegionEnter(region: InternalRegion): void {
    if (this.display().hoverEnabled !== false) {
      this.hoveredRegion.set(region);
      this.regionHover.emit(regionToMembership(region, this.sets()));
    }
  }

  onRegionLeave(): void {
    this.hoveredRegion.set(null);
    this.regionHover.emit(null);
  }

  onRegionClicked(region: InternalRegion): void {
    if (this.display().clickEnabled) {
      this.regionClick.emit(regionToMembership(region, this.sets()));
    }
  }

  // ── 3-set event handlers ──────────────────────────────

  onEnter3(key: string): void {
    if (this.display().hoverEnabled !== false) {
      this.hoveredRegion.set(key);
      this.regionHover.emit(buildMembership(key, this.renderedSets()));
    }
  }

  onClick3(key: string): void {
    if (this.display().clickEnabled) {
      this.regionClick.emit(buildMembership(key, this.renderedSets()));
    }
  }

  // ── 3-set fill/opacity (called from template) ─────────

  fill3(key: string): string {
    const customColor = this.highlightColor();
    if (this.highlightedKeys().has(key)) {
      return customColor ?? 'var(--venn-highlight)';
    }
    return 'var(--venn-bg)';
  }

  opacity3(key: string): number {
    if (this.hoveredRegion() === key) {
      return 0.8;
    }
    return 1;
  }

  // ── 2-set private helpers ─────────────────────────────

  private regionFill(region: InternalRegion): string {
    const customColor = this.highlightColor();
    if (this.highlighted2().has(region)) {
      return customColor ?? 'var(--venn-highlight)';
    }
    return 'var(--venn-bg)';
  }

  private regionOpacity(region: InternalRegion): number {
    if (this.hoveredRegion() === region) {
      return 0.8;
    }
    return 1;
  }

  private clampPAB(pA: number, pB: number): void {
    const lo = round(Math.max(0, pA + pB - 1));
    const hi = round(Math.min(pA, pB));
    const clamped = round(Math.max(lo, Math.min(this.pAB(), hi)));
    if (clamped !== this.pAB()) {
      this.pAB.set(clamped);
    }
  }
}
